import axios from 'axios';
import { AUTH_CONSTANTS } from '../utils/auth-constants';
import { logger } from './analytics';

// Função auxiliar para obter o storage correto baseado em remember_me
const getStorage = (): Storage => {
  const rememberMe = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME) === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

// Função para obter o token de autenticação (verifica em ambos os storages)
export const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN) || 
         sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
};

// Função para obter o refresh token (verifica em ambos os storages)
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN) || 
         sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
};

// Função para configurar token de autorização globalmente
// Esta é a fonte única da verdade para tokens de autenticação
export const setAuthToken = (token: string | null, rememberMe?: boolean) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const storage = rememberMe !== undefined 
      ? (rememberMe ? localStorage : sessionStorage)
      : getStorage();
    
    // Limpar de ambos os storages antes de salvar no correto
    localStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
    
    storage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN, token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.AUTH_TOKEN);
  }
};

// Função para configurar refresh token
export const setRefreshToken = (token: string | null, rememberMe?: boolean) => {
  if (token) {
    const storage = rememberMe !== undefined 
      ? (rememberMe ? localStorage : sessionStorage)
      : getStorage();
    
    // Limpar de ambos os storages antes de salvar no correto
    localStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
    
    storage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN, token);
  } else {
    localStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
  }
};

// Função para limpar todos os tokens e dados de autenticação
export const clearAllTokens = () => {
  setAuthToken(null);
  setRefreshToken(null);
  
  const keysToRemove = [
    AUTH_CONSTANTS.STORAGE_KEYS.USER_ID,
    AUTH_CONSTANTS.STORAGE_KEYS.CANDIDATE_ID,
    AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID,
    AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID,
    AUTH_CONSTANTS.STORAGE_KEYS.EM_UNIT_ID,
    AUTH_CONSTANTS.STORAGE_KEYS.LEGACY_PASSWORD_LOGIN,
    AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME,
  ];
  
  // Limpar de ambos os storages
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  sessionStorage.clear();
};

/**
 * Função centralizada para limpar todos os dados de autenticação
 * Usa logger para rastrear a ação
 */
export const clearAuthData = (reason?: string): void => {
  clearAllTokens();
  
  logger.auth('Auth data cleared', { 
    reason: reason || 'manual_logout',
    timestamp: new Date().toISOString()
  });
};

// Função para configurar tokens após login
export const setTokensAfterLogin = (accessToken: string, refreshToken: string, rememberMe: boolean = false) => {
  // Salvar a flag remember_me primeiro para que as funções de set token funcionem corretamente
  if (rememberMe) {
    localStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME, 'true');
  } else {
    localStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME);
  }
  
  // Salvar tokens no storage apropriado baseado em remember_me
  setAuthToken(accessToken, rememberMe);
  setRefreshToken(refreshToken, rememberMe);
};
